import {
    AfterContentInit,
    Component,
    computed,
    ContentChild,
    ContentChildren,
    DestroyRef,
    EventEmitter,
    input,
    model,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    signal,
    SimpleChanges,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import { Icon } from '../icon/icon.component';
import { NgForOf, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Page } from '../paginator/page.type';
import { Sorting } from '../../query/sorting';
import { Paginator } from '../paginator/paginator.component';
import '../../utils/string-extensions';
import { DataSource } from './datasource';

@Component({
    selector: 'ai-datatable-column',
    standalone: true,
    template: '',
    encapsulation: ViewEncapsulation.None,
})
/**
 * Define uma coluna do {@link DataTable}.
 *
 * Use os templates `#aiDataTableCellTemplate` (célula) e
 * `#aiDataTableHeaderTemplate` (cabeçalho) projetados no conteúdo da coluna.
 */
export class DataTableColumn {
    /** Limite de caracteres antes de truncar (-1 = sem truncamento). */
    readonly truncate = input<number>(-1);
    /** Sufixo exibido ao truncar. */
    readonly truncateTrail = input<string>('...');
    /** Largura da coluna. */
    readonly width = input<string>('auto');
    /** Campo do registro exibido na coluna. */
    readonly field = input<string>('Field');
    /** Nome alternativo do campo (alias). */
    readonly fieldAlias = input<string>();
    /** Título exibido no cabeçalho da coluna. */
    readonly title = input<string>('Title');
    /** Define se a coluna é ordenável. */
    readonly sortable = input<boolean>(false);
    /**
     * Oculta a coluna em telas de phone (≤767.98px), reduzindo a informação
     * exibida para caber sem esmagar/rolar. As colunas essenciais (ex.: nome,
     * ações) devem permanecer com o padrão `false`.
     */
    readonly hideOnMobile = input<boolean>(false);
    /** Direção de ordenação atual. */
    readonly sortingDirection = signal<'asc' | 'desc'>(Sorting.ASC);

    @ContentChild('aiDataTableCellTemplate', { static: true })
    readonly cellTemplate!: TemplateRef<any>;

    @ContentChild('aiDataTableHeaderTemplate', { static: true })
    readonly headerTemplate!: TemplateRef<any>;
}

/**
 * Tabela de dados com colunas configuráveis, paginação e ordenação.
 *
 * Os dados vêm de um {@link DataSource} (ex.: `ArrayDataSource` ou
 * `PagedQueryDataSource`). As colunas são definidas por {@link DataTableColumn}
 * projetadas no conteúdo, cada uma com seu template de célula.
 *
 * Usa `ViewEncapsulation.None` de propósito: estiliza o conteúdo **projetado**
 * (colunas/células declaradas pelo consumidor) por seletores descendentes
 * (`ai-datatable td`, `ai-datatable thead tr th`…), que precisam alcançar
 * elementos fora da view do componente.
 */
@Component({
    selector: 'ai-datatable',
    standalone: true,
    templateUrl: './datatable.component.html',
    styleUrls: ['./datatable.component.scss'],
    host: {
        class: 'ai-datatable',
        '[class.ai-datatable--truncate-text]': 'truncateText()',
        '[class.ai-datatable--scrollable]': 'scrollHeight()',
        '[style.max-height]': 'scrollHeight()',
        '[style.overflow-y]': "scrollHeight() ? 'auto' : null",
    },
    imports: [Icon, NgForOf, NgIf, NgStyle, NgTemplateOutlet, Paginator],
    encapsulation: ViewEncapsulation.None,
})
export class DataTable<T> implements OnInit, OnChanges, AfterContentInit {
    /** Quantidade de itens por página. */
    readonly pageSize = input<number>(5);
    /**
     * Opções de "itens por página" repassadas ao paginador. Vazio (padrão)
     * oculta o seletor. Ao informar valores, a troca dispara
     * {@link DataSource.changePageSize} (recarrega a partir da 1ª página).
     */
    readonly pageSizeOptions = input<number[]>([]);
    /** Fonte de dados da tabela (obrigatório). */
    readonly dataSource = input.required<DataSource<T>>();
    /** Limite global de truncamento de células (-1 = sem truncamento). */
    readonly truncate = input<number>(-1);
    /** Define se o paginador é exibido. */
    readonly showPaginator = input<boolean>(true);

    /**
     * Altura máxima da tabela (ex.: `'280px'`). Quando definida, a tabela ganha
     * rolagem vertical própria e o cabeçalho (`thead`) fica **fixo** no topo
     * enquanto apenas o corpo (`tbody`) rola. Sem valor, a tabela cresce com o
     * conteúdo (comportamento padrão).
     */
    readonly scrollHeight = input<string | null>(null);

    /**
     * Loading externo. Quando `true`, exibe o mesmo overlay de carregamento da
     * tabela usado durante as buscas — útil para fontes síncronas
     * (`ArrayDataSource`) atualizadas por uma operação de rede do container
     * (ex.: sincronizar/recarregar).
     */
    readonly loading = input<boolean>(false);

    /**
     * Controla o truncamento de texto das células/cabeçalhos (**padrão: `true`**).
     *
     * Quando `true`, as colunas respeitam a largura definida (`table-layout:
     * fixed`) e o conteúdo que excede vira **uma linha com reticências**; no
     * cabeçalho, o título trunca e os ícones de ordenação seguem visíveis.
     * Defina `[truncateText]="false"` para permitir **quebra de linha** (coluna
     * fluida) onde o texto completo precisa aparecer.
     *
     * Distinto de {@link truncate}, que corta a *string* por número de
     * caracteres; aqui o truncamento é visual (CSS), conforme a largura.
     */
    readonly truncateText = input<boolean>(true);

    @ContentChild('aiDataTableNoDataTemplate', { static: true })
    readonly noDataTemplate!: TemplateRef<any>;

    /**
     * Template opcional de uma linha fixa renderizada ao final do corpo da
     * tabela (após as linhas de dados), independentemente da quantidade de
     * registros. Útil para uma linha de adição/edição inline. O template deve
     * fornecer as células (`<td>`), que são envolvidas em um `<tr>`.
     */
    @ContentChild('aiDataTableFooterRowTemplate', { static: true })
    readonly footerRowTemplate!: TemplateRef<any>;

    @Output('sort')
    readonly sort = new EventEmitter<DataTableColumn>();

    /** Emite os dados da linha ao clicar nela (ex.: navegar para o detalhe). */
    @Output()
    readonly rowClick = new EventEmitter<T>();

    protected readonly rows = signal<TableRow[]>([]);
    protected readonly data = signal<T[]>([]);
    /** Últimos dados recebidos, usados para remontar as linhas após o content init. */
    private lastData: T[] = [];
    protected readonly total = signal<number>(0);
    protected readonly currentPage = model<number>(1);
    /**
     * Tamanho de página efetivo entregue ao paginador. Nasce de {@link pageSize}
     * e é atualizado quando o usuário troca "itens por página", para que o
     * paginador recalcule o total de páginas.
     */
    protected readonly effectivePageSize = signal<number>(5);
    protected readonly isLoading = signal<boolean>(false);

    /** Carregamento efetivo: da fonte de dados OU externo ({@link loading}). */
    protected readonly busy = computed<boolean>(
        () => this.isLoading() || this.loading(),
    );

    @ContentChildren(DataTableColumn)
    readonly columns!: QueryList<DataTableColumn>;

    constructor(private destroyRef: DestroyRef) { }

    /**
     * Hook do OnInit.
     *
     * Escuta o observable do dataSource para atualizar os dados
     * na datatable.
     */
    public async ngOnInit(): Promise<void> {
        this.effectivePageSize.set(this.pageSize());

        this.dataSource()
            .onReady()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: async (page: Page<T>) => {
                    // Define os dados ANTES de montar as linhas: fontes
                    // síncronas (ArrayDataSource) emitem ainda no ngOnInit,
                    // quando as colunas (ContentChildren) ainda não existem.
                    this.lastData = page.data;
                    this.data.set(page.data);
                    this.total.set(page.total);
                    this.currentPage.set(page.currentPage);
                    await this.extractRows(page.data);
                },
            });

        this.dataSource()
            .isLoading()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (loading: boolean) => {
                    this.isLoading.set(loading);
                },
            });
    }

    /**
     * Hook do AfterContentInit.
     *
     * As colunas (ContentChildren) só ficam disponíveis aqui. Remonta as linhas
     * caso já existam dados (necessário para fontes síncronas, cujo `onReady`
     * emite ainda no `ngOnInit`).
     */
    public async ngAfterContentInit(): Promise<void> {
        if (this.lastData.length > 0) {
            await this.extractRows(this.lastData);
        }
    }

    /**
     * Hook do OnChanges.
     * TODO Precisa?
     */
    public async ngOnChanges(changes: SimpleChanges): Promise<void> { }

    /**
     * Trata o clique nos ícones de ordenamento de uma determinada coluna.
     * Caso um Output tenha sido definido, ignora o processo automático de
     * ordenação usando o DataSource e emite o evento apenas.
     *
     * Caso um Output não tenha sido definido, realiza o processo
     * automático de filtro, usando o field() da coluna para solicitar
     * o ordenamento ao DataSource.
     *
     * @param column Coluna que deve ser usada para ordenar.
     * @param direction Direção do ordenamento.
     */
    public async onClickSortColumn(
        column: DataTableColumn,
        direction: 'asc' | 'desc',
    ): Promise<void> {
        if (!this.sort.observed) {
            this.dataSource()
                .clearSorting()
                .sortBy(column.fieldAlias() ?? column.field(), direction)
                .fetch();
        } else {
            this.sort.emit(column);
        }

        column.sortingDirection.set(direction);
    }

    /**
     * Trata o evento de mudança de páginas do component Paginator.
     * Utiliza o DataSource para solicitar a página informada pelo Paginator.
     *
     * @param page Número da página.
     */
    public async onPageChanged(page: number): Promise<void> {
        this.dataSource().goToPage(page);
    }

    /**
     * Trata a troca de "itens por página" emitida pelo paginador: atualiza o
     * tamanho efetivo e pede à fonte de dados que recarregue a partir da 1ª
     * página com o novo tamanho.
     *
     * @param pageSize Novo tamanho de página.
     */
    public async onPageSizeChanged(pageSize: number): Promise<void> {
        this.effectivePageSize.set(pageSize);
        this.dataSource().changePageSize(pageSize);
    }

    /**
     * Extrai dos dados uma lista de linhas/rows.
     * Cada Row contém uma lista de Cells, que representam o valor
     * que cada coluna terá na Row.
     *
     * @param data Dados dos quais devem ser extraídos os Rows.
     * @private
     */
    private async extractRows(data: T[]): Promise<void> {
        const rows: TableRow[] = [];

        // Pode ser chamado antes do AfterContentInit (fonte síncrona): nesse
        // caso as colunas ainda não existem e as linhas são remontadas depois.
        const columns = this.columns ?? [];

        data.forEach((item: T, index: number) => {
            rows.push({
                index: index,
                data: item,
                cells: columns.map((column: DataTableColumn) => {
                    return {
                        value: this.applyTransformations(
                            this.extractValue(item, column.field() || ''),
                            column,
                        ),
                        template: column.cellTemplate,
                        hideOnMobile: column.hideOnMobile(),
                    } as TableCell;
                }),
            });
        });

        this.rows.set(rows);
    }

    /**
     * Obtém um valor aninhado de um objeto de forma segura usando um caminho de string.
     * @param obj O objeto do qual se obterá o valor.
     * @param path O caminho para a propriedade (ex: "person.name").
     * @returns O valor encontrado ou undefined se o caminho não existir.
     */
    private extractValue(obj: any, path: string): string {
        return (
            path.split('.').reduce((acc, key) => {
                return acc?.[key];
            }, obj) || ''
        );
    }

    /**
     * Aplica possíveis restrições ou transformações que o valor a ser exibido pode sofrer.
     *
     * @param value Valor que pode ser transformado.
     * @param column Coluna na qual o valor está inserido.
     * @return
     * @private
     */
    private applyTransformations(
        value: string,
        column: DataTableColumn,
    ): string {
        if (column.truncate() && column.truncate() > 0) {
            return value.truncate(column.truncate(), column.truncateTrail());
        }

        return value;
    }
}

class TableCell {
    public value: any | null | undefined;
    public template!: TemplateRef<any>;
    /** Espelha {@link DataTableColumn.hideOnMobile} para ocultar a célula. */
    public hideOnMobile: boolean = false;
}

class TableRow {
    public index: number = 0;
    public data: any | null | undefined;
    public cells: TableCell[] = [];
}
